
import React, { useEffect } from 'react';

interface GrowthInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GrowthInfoModal: React.FC<GrowthInfoModalProps> = ({ isOpen, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-slate-100">
          <div>
            <h3 className="text-2xl font-black text-slate-900">GROWTH</h3>
            <p className="text-slate-500 text-sm font-bold mt-1">Service Breakdown</p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto p-6 md:p-8 space-y-8 text-slate-600 leading-relaxed text-sm md:text-base">
          
          <section>
            <h4 className="text-purple-600 font-black text-lg mb-2 flex items-center gap-2">
              <i className="fas fa-door-open"></i> Portals
            </h4>
            <p className="font-medium text-slate-900 mb-3">
              A portal is a private area on your website where people log in to see information just for them.
            </p>
            <div className="mb-4 pl-4 border-l-2 border-purple-100">
                <span className="text-xs font-bold text-slate-500 block mb-2">Examples:</span>
                <ul className="space-y-1.5 font-medium text-slate-700">
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Members accessing resources</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Clients viewing updates or documents</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Staff logging in to internal info</li>
                </ul>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">In plain terms</span>
                <p className="text-slate-700 font-semibold leading-relaxed">A secure online space for your people.</p>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h4 className="text-purple-600 font-black text-lg mb-2 flex items-center gap-2">
              <i className="fas fa-calendar-check"></i> Booking Systems
            </h4>
            <p className="font-medium text-slate-900 mb-3">
              A booking system lets people book events, classes, or appointments online.
            </p>
            <div className="mb-4 pl-4 border-l-2 border-purple-100">
                <span className="text-xs font-bold text-slate-500 block mb-2">Examples:</span>
                <ul className="space-y-1.5 font-medium text-slate-700">
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Cooking classes</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Events and workshops</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Consultations</li>
                </ul>
            </div>
             <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">In plain terms</span>
                <p className="text-slate-700 font-semibold leading-relaxed">An easy way for customers to book without emails or phone calls.</p>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h4 className="text-purple-600 font-black text-lg mb-2 flex items-center gap-2">
              <i className="fas fa-tools"></i> Internal Tools
            </h4>
            <p className="font-medium text-slate-900 mb-3">
              Internal tools are simple apps built just for you or your team to manage work behind the scenes.
            </p>
            <div className="mb-4 pl-4 border-l-2 border-purple-100">
                <span className="text-xs font-bold text-slate-500 block mb-2">Examples:</span>
                <ul className="space-y-1.5 font-medium text-slate-700">
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Managing content, events, or users</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Tracking enquiries or tasks</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-purple-400"></i> Updating information in one place</li>
                </ul>
            </div>
             <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">In plain terms</span>
                <p className="text-slate-700 font-semibold leading-relaxed">Tools that make running your business easier and less manual.</p>
            </div>
          </section>

        </div>

        {/* Footer Actions */}
        <div className="p-6 md:p-8 border-t border-slate-100 bg-slate-50 rounded-b-3xl flex justify-end">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrowthInfoModal;
